/**
 * Cartero mail — K1 export (client side).
 *
 * Three portable formats, all delivered as a browser download (no server
 * round-trip beyond fetching the raw bytes):
 *
 *  - **`.eml`** — one message, the faithful raw RFC822 bytes from the server
 *    cache (`GET …/messages/:uid/raw`).
 *  - **`.mbox`** — a whole folder, the concatenated raw messages framed as mbox
 *    (`GET …/export`).
 *  - **CSV** — the folder's message metadata (from/to/subject/date/folder),
 *    built from the already-loaded list through the shared K1 kit (which
 *    neutralizes spreadsheet-formula injection on the text cells).
 */

import {
  browserDelivery,
  buildMailMetadataDataset,
  downloadBlob,
  exportDataset,
  type ExportDelivery,
  type MailExportRecord,
} from "@cartero/shared";

import { mailApi } from "./api";

/** Slugify a folder name into a safe file stem (`Sent Items` → `sent-items`). */
function slugify(value: string): string {
  return (
    value
      .toLowerCase()
      .replaceAll(/[^a-z0-9]+/g, "-")
      .replaceAll(/^-+|-+$/g, "")
      .slice(0, 60) || "mailbox"
  );
}

/** Downloads a single message as a faithful `.eml` (raw RFC822 bytes). */
export async function downloadMessageEml(
  accountId: string,
  uid: number,
  folder: string,
): Promise<void> {
  const blob = await mailApi.getRawMessage(accountId, uid, folder);
  downloadBlob(`message-${uid}.eml`, blob);
}

/** Downloads a whole folder as a faithful `.mbox` (concatenated raw messages). */
export async function downloadFolderMbox(accountId: string, folder: string): Promise<void> {
  const blob = await mailApi.exportFolderMbox(accountId, folder);
  downloadBlob(`${slugify(folder)}.mbox`, blob);
}

/**
 * Downloads the folder's message metadata as a CSV. Pure client-side from the
 * loaded list — no raw bytes needed — through the shared K1 kit.
 */
export function downloadFolderCsv(
  messages: readonly MailExportRecord[],
  folder: string,
  accountEmail?: string,
  delivery: ExportDelivery = browserDelivery,
): void {
  const dataset = buildMailMetadataDataset(messages, {
    title: folder,
    subtitle: accountEmail,
  });
  void exportDataset(dataset, "csv", delivery);
}
